import React, { useEffect, useState } from "react";
import axios from "axios";
import { Base_URL } from "../url";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
import { createPortal } from "react-dom";
import "../Styling/VendorInvoiceList.css";

export default function VendorInvoicesList() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const [expanded, setExpanded] = useState({});
  const [itemsLoading, setItemsLoading] = useState({});
  const [posting, setPosting] = useState({});
  const [gstData, setGstData] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [reversing, setReversing] = useState(false);

  const { user } = useAuth();

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const resp = await axios.get(
          `${Base_URL}/VendorInvoice/GetVendorInvoiceHeader/${user?.userId}`
        );
        if (!mounted) return;
        setInvoices(Array.isArray(resp.data) ? resp.data : []);
        setError(null);
      } catch (err) {
        console.error(err);
        setError("Failed to load vendor invoices");
        toast.error("Failed to load vendor invoices");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => (mounted = false);
  }, [showModal]);

  const formatDate = (iso) => {
    if (!iso) return "-";
    try {
      return new Date(iso).toLocaleDateString();
    } catch (e) {
      return String(iso).split("T")[0];
    }
  };

  const fetchGstDataForItem = async (itemCode) => {
    if (!itemCode) return { CGST: 0, IGST: 0, SGST: 0 };
    try {
      const response = await axios.get(
        `${Base_URL}/VendorInvoice/GetGst?itemCode=${encodeURIComponent(itemCode)}`
      );
      if (response.data) {
        const data = response.data;
        let gstRates = { CGST: 0, IGST: 0, SGST: 0 };
        if (data.CGST_Percent !== undefined) {
          gstRates = {
            CGST: parseFloat(data.CGST_Percent) || 0,
            IGST: parseFloat(data.IGST_Percent) || 0,
            SGST: parseFloat(data.SGST_Percent) || 0,
          };
        } else if (Array.isArray(data) && data.length > 0) {
          gstRates = {
            CGST: parseFloat(data[0].CGST_Percent) || 0,
            IGST: parseFloat(data[0].IGST_Percent) || 0,
            SGST: parseFloat(data[0].SGST_Percent) || 0,
          };
        } else if (data.cgsT_Percent !== undefined) {
          gstRates = {
            CGST: parseFloat(data.cgsT_Percent) || 0,
            IGST: parseFloat(data.igsT_Percent) || 0,
            SGST: parseFloat(data.sgsT_Percent) || 0,
          };
        }
        return gstRates;
      }
      return { CGST: 0, IGST: 0, SGST: 0 };
    } catch (error) {
      return { CGST: 0, IGST: 0, SGST: 0 };
    }
  };

  const calculateGstAmounts = (basicValue, cgstRate, igstRate, sgstRate) => {
    if (!basicValue) return { cgstAmount: 0, igstAmount: 0, sgstAmount: 0, totalAmount: 0 };
    const amount = parseFloat(basicValue);
    const cgstAmount = (amount * (cgstRate || 0)) / 100;
    const igstAmount = (amount * (igstRate || 0)) / 100;
    const sgstAmount = (amount * (sgstRate || 0)) / 100;
    const totalAmount = amount + cgstAmount + igstAmount + sgstAmount;
    return {
      cgstAmount: parseFloat(cgstAmount.toFixed(2)),
      igstAmount: parseFloat(igstAmount.toFixed(2)),
      sgstAmount: parseFloat(sgstAmount.toFixed(2)),
      totalAmount: parseFloat(totalAmount.toFixed(2)),
    };
  };

  const fetchInvoiceItems = async (vendorInvoiceNumber) => {
    try {
      setItemsLoading((prev) => ({ ...prev, [vendorInvoiceNumber]: true }));
      const url = `${Base_URL}/VendorInvoice/VendorInvoiceItems/id?id=${encodeURIComponent(vendorInvoiceNumber)}`;
      const resp = await axios.get(url);
      const data = Array.isArray(resp.data) ? resp.data : [];

      const itemsWithGst = await Promise.all(
        data.map(async (item) => {
          if (item.itemCode) {
            const gstRates = await fetchGstDataForItem(item.itemCode);
            const gstAmounts = calculateGstAmounts(
              item.basicValue || 0,
              gstRates.CGST,
              gstRates.IGST,
              gstRates.SGST
            );
            return {
              ...item,
              cgstRate: gstRates.CGST,
              igstRate: gstRates.IGST,
              sgstRate: gstRates.SGST,
              calculatedCgstValue: gstAmounts.cgstAmount,
              calculatedIgstValue: gstAmounts.igstAmount,
              calculatedSgstValue: gstAmounts.sgstAmount,
              calculatedTotalValue: gstAmounts.totalAmount,
            };
          }
          return item;
        })
      );

      setExpanded((prev) => ({ ...prev, [vendorInvoiceNumber]: itemsWithGst }));
    } catch (err) {
      toast.error("Failed to load vendor invoice items");
    } finally {
      setItemsLoading((prev) => ({ ...prev, [vendorInvoiceNumber]: false }));
    }
  };

  const handleRowClick = (vendorInvoiceNumber) => {
    if (expanded[vendorInvoiceNumber]) {
      setExpanded((prev) => {
        const copy = { ...prev };
        delete copy[vendorInvoiceNumber];
        return copy;
      });
    } else {
      fetchInvoiceItems(vendorInvoiceNumber);
    }
  };

  const handlePost = async (vendorInvoiceNumber) => {
    try {
      setPosting((prev) => ({ ...prev, [vendorInvoiceNumber]: true }));
      await axios.put(
        `${Base_URL}/VendorInvoice/id?id=${encodeURIComponent(vendorInvoiceNumber)}`
      );
      toast.success(`Invoice ${vendorInvoiceNumber} posted successfully!`);
      setInvoices((prev) =>
        prev.map((inv) =>
          inv.vendorInvoice.vendorInvoiceNumber === vendorInvoiceNumber
            ? { ...inv, vendorInvoice: { ...inv.vendorInvoice, postedStatus: "Yes" } }
            : inv
        )
      );
    } catch (err) {
      toast.error(
        `Failed to post invoice ${vendorInvoiceNumber}: ${err.response?.data || err.message}`
      );
    } finally {
      setPosting((prev) => ({ ...prev, [vendorInvoiceNumber]: false }));
    }
  };

  const handleReversalConfirm = async () => {
    if (!selectedInvoice) return;
    try {
      setReversing(true);
      const payload = {
        source: "Vendor invoice reversal",
        referenceNo: selectedInvoice,
        userId: user?.userId,
        orgId: user?.organisationId,
      };
      await axios.post(`${Base_URL}/VendorInvoice/vendorInvoiceReversal`, payload);
      toast.success(`Invoice ${selectedInvoice} reversed successfully`);
      setShowModal(false);
      setSelectedInvoice(null);
    } catch (err) {
      toast.error(err.response?.data || "Failed to reverse invoice");
    } finally {
      setReversing(false);
    }
  };

  return (
    <div className="vil-page">
      <div style={{ maxWidth: "1280px", margin: "0 auto" }}>

        {/* ── Header ── */}
        <div className="vil-header">
          <h1 className="vil-title">Vendor Invoices</h1>
          <ul className="vil-actions">
            <li
              className="vil-action-link"
              onClick={() => navigate("/vendorInvoices")}
            >
              + Create Invoice With PO
            </li>
            <li
              className="vil-action-link"
              onClick={() => navigate("/vendorInvoiceWithoutPO")}
            >
              + Create Invoice Without PO
            </li>
          </ul>
        </div>

        {/* ── Table Card ── */}
        <div className="vil-card">
          <table className="vil-table">
            <thead>
              <tr>
                <th>Vendor Invoice</th>
                <th>Vendor Name</th>
                <th>Purchase Order</th>
                <th>External Invoice No.</th>
                <th>Company Code</th>
                <th>Invoice Type</th>
                <th>Posted</th>
                <th>Reversed</th>

                <th>Invoice Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>

              {/* Loading */}
              {loading && (
                <tr className="vil-state-row">
                  <td colSpan={9}>
                    <span className="vil-loading-spinner" />
                    Loading invoices…
                  </td>
                </tr>
              )}

              {/* Empty */}
              {!loading && invoices.length === 0 && (
                <tr className="vil-state-row">
                  <td colSpan={9}>No invoices found</td>
                </tr>
              )}

              {/* Data rows */}
              {!loading &&
                invoices.map((inv, idx) => {
                  const invNum = inv?.vendorInvoice?.vendorInvoiceNumber;
                  const isExpanded = !!expanded[invNum];
                  const isPosted =
                    String(inv?.vendorInvoice?.postedStatus)?.toLowerCase() === "yes";
                  const isReversed=String(inv?.vendorInvoice?.reversalStatus)?.toLowerCase() === "yes";

                  return (
                    <React.Fragment key={idx}>
                      <tr
                        className="vil-row"
                        onClick={(e) => {
                          if (e.target.closest("button")) return;
                          handleRowClick(invNum);
                        }}
                      >
                        <td>
                          <span className="vil-invoice-num">{invNum ?? "-"}</span>
                        </td>
                        <td>
                          <span className="vil-vendor-name">
                            {inv?.vendor?.name ?? "-"}
                          </span>
                        </td>
                        <td>{inv?.vendorInvoice?.purchaseOrderNumber ?? "-"}</td>
                        <td>{inv?.vendorInvoice?.externalInvoiceNumber ?? "-"}</td>
                        <td>{String(inv?.company?.name ?? "-")}</td>
                        <td>{inv?.vendorInvoice?.invoiceType ?? "-"}</td>
                        <td>
                          {isPosted ? (
                            <span className="vil-badge vil-badge--yes">Yes</span>
                          ) : (
                            <span className="vil-badge vil-badge--no">No</span>
                          )}
                        </td>
                        <td>
                          {isReversed ? (
                            <span className="vil-badge vil-badge--yes">Yes</span>
                          ) : (
                            <span className="vil-badge vil-badge--no">No</span>
                          )}
                        </td>
                        <td>{formatDate(inv?.vendorInvoice?.createdOn)}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="vil-actions-cell">

                            {/* Post Button */}
                            <button
                              className={`vil-btn ${
                                isPosted
                                  ? "vil-btn--posted"
                                  : posting[invNum]
                                  ? "vil-btn--posting"
                                  : "vil-btn--post"
                              }`}
                              onClick={() => handlePost(invNum)}
                              disabled={isPosted || posting[invNum]}
                            >
                              {posting[invNum] ? "Posting…" : isPosted ? "Posted" : "Post"}
                            </button>

                            {/* Edit Button */}
                            <button
                              className="vil-btn vil-btn--edit"
                              onClick={() =>
                                navigate(`/editVendorInvoice/${invNum}`)
                              }
                            >
                              Edit
                            </button>

                            {/* Reverse Button */}
                            <button
                              className="vil-btn vil-btn--reverse"
                              disabled={isReversed}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedInvoice(invNum);
                                setShowModal(true);
                              }}
                            >
                              Reverse
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Expanded sub-row */}
                      {isExpanded && (
                        <tr className="vil-expanded-row">
                          <td colSpan={9}>
                            {itemsLoading[invNum] ? (
                              <div className="vil-sub-loading">
                                <span className="vil-loading-spinner" />
                                Loading items…
                              </div>
                            ) : (
                              <div className="vil-expanded-inner">
                                <h4>Invoice Items &amp; GST Breakdown</h4>
                                <table className="vil-sub-table">
                                  <thead>
                                    <tr>
                                      <th>Item Code</th>
                                      <th>Description</th>
                                      <th>Orig. Qty</th>
                                      <th>Recv. Qty</th>
                                      <th>Inv. Qty</th>
                                      <th>Balance</th>
                                      <th>Unit</th>
                                      <th>Price</th>
                                      <th>Basic Value</th>
                                      <th>CGST %</th>
                                      <th>CGST Value</th>
                                      <th>SGST %</th>
                                      <th>SGST Value</th>
                                      <th>IGST %</th>
                                      <th>IGST Value</th>
                                      <th>Total Value</th>
                                      <th>Receipt Date</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {expanded[invNum]?.map((item, itemIndex) => (
                                      <tr key={itemIndex}>
                                        <td className="item-code">{item.itemCode}</td>
                                        <td>{item.description}</td>
                                        <td className="text-right">{item.originalQuantity}</td>
                                        <td className="text-right">{item.quantityReceived}</td>
                                        <td className="text-right">{item.invoiceQuantity}</td>
                                        <td className="text-right">{item.balanceQuantity}</td>
                                        <td>{item.unit}</td>
                                        <td className="text-right">{item.price?.toFixed(2)}</td>
                                        <td className="text-right">{item.basicValue?.toFixed(2)}</td>
                                        <td className="text-right cgst-cell">{item.cgst?.toFixed(2)}%</td>
                                        <td className="text-right cgst-cell">{item.cgstValue?.toFixed(2)}</td>
                                        <td className="text-right sgst-cell">{item.sgst?.toFixed(2)}%</td>
                                        <td className="text-right sgst-cell">{item.sgstValue?.toFixed(2)}</td>
                                        <td className="text-right igst-cell">{item.igst?.toFixed(2)}%</td>
                                        <td className="text-right igst-cell">{item.igstValue?.toFixed(2)}</td>
                                        <td className="text-right total-cell">
                                          {(
                                            (Number(item.basicValue) || 0) +
                                            (Number(item.sgstValue ?? item.calculatedSgstValue ?? item.sgst ?? 0) || 0) +
                                            (Number(item.cgstValue ?? item.calculatedCgstValue ?? item.cgst ?? 0) || 0) +
                                            (Number(item.igstValue ?? item.calculatedIgstValue ?? item.igst ?? 0) || 0)
                                          ).toFixed(2)}
                                        </td>
                                        <td>{formatDate(item.receiptDate)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
            </tbody>
          </table>
        </div>

        {error && <div className="vil-error">{error}</div>}
      </div>

      {/* ── Reversal Confirmation Modal (Portal) ── */}
      {showModal &&
        createPortal(
          <div className="vil-modal-overlay">
            <div className="vil-modal">
              {/* Warning Icon */}
              <div className="vil-modal__icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>

              <h2 className="vil-modal__title">Confirm Reversal</h2>
              <p className="vil-modal__body">
                Are you sure you want to reverse the posting for invoice{" "}
                <strong>{selectedInvoice}</strong>? This action cannot be undone.
              </p>

              <div className="vil-modal__footer">
                <button
                  className="vil-modal__btn vil-modal__btn--cancel"
                  onClick={() => {
                    setShowModal(false);
                    setSelectedInvoice(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="vil-modal__btn vil-modal__btn--confirm"
                  onClick={handleReversalConfirm}
                  disabled={reversing}
                >
                  {reversing ? "Reversing…" : "Yes, Reverse"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}